import { WORDLIST } from '~/constants/bip39-wordlist'
import { arrayBufferToBase64, arrayBufferToHex, bytesToBits } from '~/utils/crypto'

/**
 * Composable for BIP39 seed phrase generation, validation, and credential derivation.
 *
 * The seed phrase is the user's single secret. From it, three independent values
 * are deterministically derived via HKDF with domain-separated salts:
 *
 * 1. **Email identity** — a hex-based anonymous email (`<hash>@anon.minimind.es`)
 *    used as the Supabase auth identifier. Contains no personal information.
 * 2. **Password** — a 256-bit base64 string for Supabase auth.
 * 3. **Encryption key** — an AES-GCM 256-bit CryptoKey used by `useEncryption`
 *    to encrypt/decrypt all sensitive content client-side.
 *
 * Because derivation is deterministic, the same seed always produces the same
 * credentials, allowing the user to log in from any device without storing
 * anything server-side. The seed is never transmitted or persisted.
 */
export function useSeed() {
  /**
   * Generates a 12-word BIP39 mnemonic seed phrase.
   * Uses 128 bits of entropy + 4-bit SHA-256 checksum = 132 bits → 12 words.
   */
  async function generateSeed(): Promise<string> {
    // 1. Generate 128 bits of entropy
    const entropy = crypto.getRandomValues(new Uint8Array(16))

    // 2. Compute SHA-256 checksum
    const hash = await crypto.subtle.digest('SHA-256', entropy)
    const hashBytes = new Uint8Array(hash)

    // 3. Append first 4 bits of checksum to entropy (132 bits total)
    const bits = bytesToBits(entropy) + bytesToBits(hashBytes).slice(0, 4)

    // 4. Split into 12 groups of 11 bits → map to wordlist
    const words: string[] = []
    for (let i = 0; i < 12; i++) {
      const index = parseInt(bits.slice(i * 11, (i + 1) * 11), 2)
      words.push(WORDLIST[index]!)
    }

    return words.join(' ')
  }

  /**
   * Validates a seed phrase: 12 words, all in wordlist, valid checksum.
   */
  async function validateSeed(seed: string): Promise<boolean> {
    const words = seed.trim().toLowerCase().split(/\s+/)
    if (words.length !== 12) return false

    // Check all words exist in wordlist
    const indices = words.map((w) => WORDLIST.indexOf(w))
    if (indices.some((i) => i === -1)) return false

    // Reconstruct bits
    const bits = indices.map((i) => i.toString(2).padStart(11, '0')).join('')

    // Split: first 128 bits = entropy, last 4 bits = checksum
    const entropyBits = bits.slice(0, 128)
    const checksumBits = bits.slice(128)

    // Convert entropy bits back to bytes
    const entropyBytes = new Uint8Array(16)
    for (let i = 0; i < 16; i++) {
      entropyBytes[i] = parseInt(entropyBits.slice(i * 8, (i + 1) * 8), 2)
    }

    // Recompute checksum
    const hash = await crypto.subtle.digest('SHA-256', entropyBytes)
    const hashBits = bytesToBits(new Uint8Array(hash))

    return hashBits.slice(0, 4) === checksumBits
  }

  /**
   * Derives Supabase credentials, encryption key, and wrapper key from a seed phrase using HKDF.
   * Each derivation uses independent salt/info for cryptographic domain separation.
   *
   * The `encryptionKey` is the legacy key used only during migration of existing accounts.
   * The `wrapperKey` is the AES-KW key used to wrap/unwrap the real encryption key.
   */
  async function deriveCredentials(seed: string): Promise<{
    email: string
    password: string
    encryptionKey: CryptoKey
    wrapperKey: CryptoKey
  }> {
    const enc = new TextEncoder()

    // Import seed as HKDF key material
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      enc.encode(seed),
      'HKDF',
      false,
      ['deriveBits', 'deriveKey']
    )

    // Derive email identity (32 bytes → hex → fake email)
    const emailBits = await crypto.subtle.deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: enc.encode('minimind-v2-email'),
        info: enc.encode('email-identity')
      },
      keyMaterial,
      256
    )
    const emailHex = arrayBufferToHex(emailBits)
    const email = `${emailHex}@anon.minimind.es`

    // Derive password (32 bytes → base64)
    const passwordBits = await crypto.subtle.deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: enc.encode('minimind-v2-password'),
        info: enc.encode('auth-password')
      },
      keyMaterial,
      256
    )
    const password = arrayBufferToBase64(passwordBits)

    // Derive legacy encryption key (AES-GCM 256-bit) — used only for migration
    const encryptionKey = await crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: enc.encode('minimind-v2-encryption'),
        info: enc.encode('data-encryption-key')
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    )

    // Derive wrapper key (AES-KW 256-bit) — wraps/unwraps the real encryption key
    const wrapperKey = await crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: enc.encode('minimind-v2-wrapper'),
        info: enc.encode('key-wrapping')
      },
      keyMaterial,
      { name: 'AES-KW', length: 256 },
      false,
      ['wrapKey', 'unwrapKey']
    )

    return { email, password, encryptionKey, wrapperKey }
  }

  return {
    generateSeed,
    validateSeed,
    deriveCredentials
  }
}
